import { countDecimals, getNodeResult, getValueAtDate } from "@imminently/decisively-core";
import { isValid, parseISO } from "date-fns";
import React, { type ReactNode } from "react";
import TemporalValue from "../components/blocks/TemporalValue/TemporalValue";

export const NodeDisplayType = {
  UNKNOWN: "Unknown",
  UNCERTAIN: "Uncertain",
  TEMPORAL: "Temporal",
  BOOLEAN: "Boolean",
  STRING: "String",
  NUMBER: "Number",
  ENTITY: "Entity",
  FILE: "File",
  DATE: "Date",
  TEST_OUTCOME: "TestOutcome",
  SET: "Set",
} as const;

export type TNodeDisplayType = (typeof NodeDisplayType)[keyof typeof NodeDisplayType];

export interface TemporalTextProps {
  maxEntries?: number;
  scrollable?: boolean;
}

const roundToMaxDigitsAfterDot = (v: number, maxDigitsAfterDot = 4): number => {
  return Number(v.toFixed(maxDigitsAfterDot));
};

// Basic neutral color mapping suitable for SDK defaults (fallback when variant not used)
const colors = {
  text: "#111827",
  mutedText: "#374151",
  defaultBg: "#F9FAFB",
};

const isClearToUnknownValue = (value: unknown): boolean => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  return keys.length === 0 || (keys.length === 1 && (value as Record<string, unknown>)["@clear"] === true);
};

export type ChipVariantKey = "success" | "destructive" | "info" | "subtle";

export interface NodeDisplayData {
  value: any;
  type: string;
  display: any;
  tooltip: string;
  color: string;
  backgroundColor: string;
  /** When set, use Chip variant (CSS vars) instead of color/backgroundColor */
  variant?: ChipVariantKey;
}

export const getNodeDisplayData = (
  node: any,
  temporalSlice: string | undefined = undefined,
  opts: { temporalTextProps?: TemporalTextProps; temporalChartHeight?: number } = {},
): NodeDisplayData => {
  let value = getNodeResult(node);
  if (isClearToUnknownValue(value)) {
    value = undefined;
  }
  let type: TNodeDisplayType = NodeDisplayType.UNKNOWN;
  let display: ReactNode = NodeDisplayType.UNKNOWN;
  let tooltip = "";
  let color: string = colors.text;
  let backgroundColor: string = colors.defaultBg;

  if (typeof value !== "undefined") {
    if (value === null) {
      display = NodeDisplayType.UNCERTAIN;
      type = NodeDisplayType.UNCERTAIN;
    } else if (value === "pass" || value === "fail") {
      const v = String(value);
      display = v.charAt(0).toUpperCase() + v.slice(1);
      type = NodeDisplayType.TEST_OUTCOME;
    } else if (typeof value === "string") {
      const valueLower = value.toLowerCase();
      if (valueLower === "true" || valueLower === "false") {
        value = valueLower === "true";
        display = String(value);
        type = NodeDisplayType.BOOLEAN;
      } else if (node?.type === "date") {
        display = value.replace(/T.*$/, "");
        type = NodeDisplayType.DATE;
      } else if (value.startsWith("data:")) {
        value = { fileRefs: [value] };
        display = "File";
        type = NodeDisplayType.FILE;
      } else if (isValid(parseISO(value))) {
        display = value;
        type = NodeDisplayType.DATE;
      } else {
        display = `"${value}"`;
        type = NodeDisplayType.STRING;
      }
    } else if (typeof value === "number") {
      display = roundToMaxDigitsAfterDot(value);
      type = NodeDisplayType.NUMBER;
      if (countDecimals(value) > 4) {
        tooltip = countDecimals(value) >= 12 ? value.toFixed(12) : String(value);
      }
    } else if ((value as any)?.temporal) {
      type = NodeDisplayType.TEMPORAL;
      if (temporalSlice) {
        const slicedValue = getValueAtDate((value as any).temporal, temporalSlice) as any;
        const result = getNodeDisplayData({ derived: slicedValue }, undefined, opts);
        result.tooltip = `Displaying temporal value at ${new Date(temporalSlice).toISOString().slice(0, 10)}`;
        return result;
      }
      display = (
        <TemporalValue
          node={node}
          value={value}
          textProps={opts.temporalTextProps}
          temporalChartHeight={opts.temporalChartHeight}
        />
      );
    } else if (Array.isArray((value as any).fileRefs)) {
      display = "File";
      type = NodeDisplayType.FILE;
    } else if (Array.isArray((value as any).set)) {
      display = "Set";
      type = NodeDisplayType.SET;
    } else if (typeof value === "boolean") {
      display = String(value);
      type = NodeDisplayType.BOOLEAN;
    } else {
      display = String(value);
      type = NodeDisplayType.ENTITY;
    }
  }

  let variant: NodeDisplayData["variant"];
  switch (type as TNodeDisplayType) {
    case NodeDisplayType.BOOLEAN:
      variant = value ? "success" : "destructive";
      color = colors.text;
      backgroundColor = colors.defaultBg;
      break;
    case NodeDisplayType.UNCERTAIN:
    case NodeDisplayType.UNKNOWN:
      variant = "subtle";
      color = colors.text;
      backgroundColor = colors.defaultBg;
      break;
    case NodeDisplayType.TEST_OUTCOME:
      variant = value === "pass" ? "success" : "destructive";
      color = colors.text;
      backgroundColor = colors.defaultBg;
      break;
    default:
      variant = "subtle";
      color = colors.mutedText;
      backgroundColor = colors.defaultBg;
      break;
  }

  return { value, type, display, tooltip, color, backgroundColor, variant };
};
